import Form                         from '@widesk-ui/components/form/Form';
import useApiCollection             from '@widesk-core/hooks/useApiCollection';
import TypeModel                    from '@models/file/StonecodeFileTypeModel';
import TextArea                     from '@widesk-ui/components/form/TextArea';
import React                        from 'react';
import TypeMimeTypeModel            from '@models/file/StonecodeFileTypeMimeTypeModel';
import FileUpload                   from '@widesk-ui/components/form/FileUpload';
import { getIdFromUrn }             from '@widesk-core/tools/identifier';
import MimeTypeModel                from '@models/file/StonecodeFileMimeTypeModel';
import FileModel                    from '@models/file/StonecodeFileFileModel';
import View                         from '@widesk-ui/components/View';
import useTheme                     from '@widesk-ui/hooks/useTheme';
import { FormItem }                 from '@widesk-ui/components/form/FormItem';
import { getFileImageRequirements } from '@/helpers/FileImageRequirementsHelper';
import { AlertProps }               from '@widesk-ui/components/Alert';
import Alert                        from '@widesk-ui/components/Alert';
import SelectStonecodeFileType      from '@selects/file/SelectStonecodeFileType';

export type FileFormProps = {
	alert?: AlertProps;
	callback?: (files: File[]) => void; // Si un callback est fourni, aucun FileModel ne sera créée, on utilisera le callback pour récupérer le/les Files.
	hideDescription?: boolean; // Ne pas afficher les inputs optionnels de description,
	hideType?: boolean; // Ne pas afficher l'input de type de fichier
	onSuccess?: () => void;
	// `rawFiles[i].type` est le mime type natif (`File.type`) du fichier `i` tel que sélectionné par
	// l'utilisateur — `FileModel` (la ressource API créée) ne l'expose pas de façon fiable/synchrone,
	// voir son appel dans `onSubmit` ci-dessous.
	onSuccessFiles?: (files: FileModel[], rawFiles: File[]) => void;
	typeReference: File_StonecodeFileTypeReference | File_StonecodeFileTypeReference[]; // Références de type de fichiers à uploader
	showImgEditor?: boolean; // Affiche ou non l'éditeur d'image (si le fichier est une image)
	forceSvgToHaveWidthAndHeight?: boolean; // Affiche ou non l'éditeur d'image (si le fichier est une image)
	sourceUrn: Urn;
	required?: boolean;
	submitOnChange?: boolean;
}

export default function FileForm(props: FileFormProps) {
	const collType = useApiCollection(TypeModel);
	const collTypeMimeType = useApiCollection(TypeMimeTypeModel);
	const collMimeType = useApiCollection(MimeTypeModel);

	const hasMultipleType = React.useMemo(() => Array.isArray(props.typeReference), []);

	const [mimeTypeReferences, setMimeTypeReferences] = React.useState<FileStonecodeFileMimeType.Reference[]>();
	const [disableUpload, setDisableUpload] = React.useState(hasMultipleType);

	const [multiple, setMultiple] = React.useState(true);
	const [imageRequirements, setImageRequirements] = React.useState<ImageRequirements>();
	const [fileNames, setFileNames] = React.useState<string[]>();

	return (
		<Form
			onSuccess={props.onSuccess}
			onDidMount={async formTools => {
				const { typeReference } = props;

				await collType.listBy(typeReference, 'reference');
				await collMimeType.listBy(typeReference, 'typeMimeTypes.type.reference');

				if (!Array.isArray(typeReference)) {
					const type = collType.first();

					if (type) {
						formTools.setFieldValue('type', type.urn);
						if (type.maxPerSource && type.maxPerSource === 1) {
							setMultiple(false);
						}
						if (collMimeType.length) {
							setMimeTypeReferences(collMimeType.map(mt => mt.reference));
						}

						const imageRequirements = getFileImageRequirements(type.reference);
						if (imageRequirements) {
							setImageRequirements(imageRequirements);
						}
					}
				}

				await collTypeMimeType.listBy(collType.ids, 'type');
			}}
			fields={tools => ({
				alert: {
					element: <Alert {...props.alert} />,
					hidden: !props.alert,
					noFormItem: true,
					span: 24,
				},
				type: {
					disabled: !Array.isArray(props.typeReference),
					colStyle: props.hideType ? { display: 'none' } : undefined,
					element: <SelectStonecodeFileType filters={{ reference: props.typeReference }} onChange={typeUrn => {
						const typeId = getIdFromUrn(typeUrn as Urn);
						const mtIds = collTypeMimeType
							.filter(tmt => tmt.getId('type') === parseInt(typeId))
							.map(tmt => tmt.getId('mimeType'));

						const mtReferences = collMimeType
							.filter(mt => mtIds.includes(mt.id))
							.map(mt => mt.reference);

						const currentType = collType.filter(t => t.urn === typeUrn)[0];
						if (currentType) {
							if (!multiple && (currentType?.maxPerSource > 1 || currentType?.maxPerSource === 0)) {
								setMultiple(true);
							}
							if (multiple && currentType.maxPerSource === 1) {
								setMultiple(false);
							}

							const imageRequirements = getFileImageRequirements(currentType.reference);
							if (imageRequirements) {
								setImageRequirements(imageRequirements);
							}
						}

						console.log({ mtReferences });

						setFileNames(undefined);
						setMimeTypeReferences(mtReferences);
						setDisableUpload(false);
					}} />,
					label: `Type`,
					required: props.required,
					span: 24,
				},
				files: {
					element: (
						<FileUpload
							forceSvgToHaveWidthAndHeight={props.forceSvgToHaveWidthAndHeight}
							imageRequirements={imageRequirements}
							onFileListChange={(fileList) => {
								setFileNames(fileList.map(file => file.name));

								if (props.submitOnChange) {
									tools.submit();
								}
							}}
							mimeTypeReferences={mimeTypeReferences as unknown as string[]}
							multiple={multiple}
							showImgEditor={props.showImgEditor}
						/>
					),
					label: `Fichier`,
					disabled: disableUpload,
					span: 24,
					required: props.required,
				},
				descriptions: {
					element: <FileDescriptions fileNames={fileNames} />,
					hidden: props.hideDescription,
					noFormItem: true,
					span: 24,
					required: props.required,
				},
			})}
			onSubmit={async (values) => {
				const { sourceUrn } = props;

				const descriptions = values.descriptions as { [key: string]: string };
				const filesValue = values.files as { fileList: File[] };

				if (!props.callback) {
					const newFiles = await Promise.all([
						...filesValue.fileList.map((file) => {
							const model = new FileModel();

							const descriptionAttribute = !props.hideDescription && descriptions[file.name]
								? { description: descriptions[file.name] }
								: {};

							return model.patchFormData({
								file: file,
								sourceUrn: sourceUrn,
								type: values.type,
								...descriptionAttribute,
							});
						}),
					]);

					if (props.onSuccessFiles) {
						props.onSuccessFiles(newFiles, filesValue.fileList);
					}

				} else {
					props.callback(filesValue.fileList);
				}
			}}
		/>
	);
}

function FileDescriptions(props: { fileNames?: string[] }) {
	const theme = useTheme();

	return (
		<View gap={theme.marginLG}>
			{props.fileNames?.map(fileName => (
				<FormItem
					label={`Description "${fileName}"`}
					key={fileName}
					name={['descriptions', fileName]}
				>
					<TextArea />
				</FormItem>
			))}
		</View>
	);
}
